import { useMutation } from '@tanstack/react-query'
import { useLiveQuery } from 'dexie-react-hooks'
import { strapiGet, strapiPost, strapiPut, strapiDelete } from '@/lib/strapi'
import { db } from '@/lib/db/database'
import type { Area } from '@/lib/types/area'
import { useAppStore } from '@/lib/store/useAppStore'
import { toast } from 'sonner'
import { format } from 'date-fns'

function wrapList<T>(data: T[] | undefined) {
  return {
    data: data !== undefined ? { data, meta: { pagination: { total: data.length, page: 1, pageSize: data.length, pageCount: 1 } } } : undefined,
    isLoading: data === undefined,
  }
}

export function useAreas() {
  const data = useLiveQuery(() => db.areas.toArray().then((r) => r.sort((a, b) => a.name.localeCompare(b.name))))
  return wrapList(data)
}

export function useCreateArea() {
  return useMutation({
    mutationFn: async (data: Partial<Area>) => {
      const restaurantId = useAppStore.getState().restaurantId
      const resp = await strapiPost<Area>('/api/areas', { data: { ...data, restaurant: restaurantId } })
      const area = (resp as any).data ?? resp
      if (area?.documentId) await db.areas.put(area)
      return resp
    },
    onSuccess: () => toast.success('Area created'),
    onError: () => toast.error('Failed to create area'),
  })
}

export function useUpdateArea() {
  return useMutation({
    mutationFn: async ({ documentId, ...data }: { documentId: string } & Partial<Area>) => {
      const resp = await strapiPut<Area>(`/api/areas/${documentId}`, { data })
      const area = (resp as any).data ?? resp
      if (area?.documentId) await db.areas.put(area)
      return resp
    },
    onSuccess: () => toast.success('Area updated'),
    onError: () => toast.error('Failed to update area'),
  })
}

export function useDeleteArea() {
  return useMutation({
    mutationFn: async ({ id, documentId }: { id: number; documentId: string }) => {
      const today = format(new Date(), 'yyyy-MM-dd')
      const check = await strapiGet<any[]>('/api/bookings', {
        filters: {
          date: { $gte: today },
          bookingTables: { table: { area: { id: { $eq: id } } } },
        },
        pagination: { pageSize: 1 },
      })
      const count = (check as any)?.meta?.pagination?.total ?? (check as any)?.data?.length ?? 0
      if (count > 0) {
        throw new Error(`This area has tables with ${count} upcoming booking(s) and cannot be deleted.`)
      }
      const result = await strapiDelete(`/api/areas/${documentId}`)
      await db.areas.delete(documentId)
      // Remove tables that belonged to this area
      await db.floorTables.where('documentId').anyOf(
        (await db.floorTables.toArray()).filter(t => t.area?.documentId === documentId).map(t => t.documentId)
      ).delete()
      return result
    },
    onSuccess: () => toast.success('Area deleted'),
    onError: (err: Error) => toast.error(err.message || 'Failed to delete area'),
  })
}
